import { Injectable, inject } from '@angular/core';
import { ToastService } from '../../../services/toast.service';
import { Company } from '../../services/company.service';
import { ImpersonationApiService } from './impersonation-api.service';
import { ImpersonationService } from './impersonation.service';
import {
  handleImpersonationApiResult,
  notifyImpersonationFailure,
} from '../utils/impersonation-ui.util';

/**
 * Starts an admin impersonation session for a company.
 *
 * Two places offer this — the row menu on the Companies list and the Impersonate
 * tab inside a company — so the API call, session handoff and failure toasts live
 * here rather than being written twice. Callers own their own confirm UI.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyAdminImpersonationService {
  private readonly api = inject(ImpersonationApiService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly messageService = inject(ToastService);

  /** The company id currently being opened, so callers can disable their trigger. */
  busyCompanyId: string | null = null;

  /** Admin impersonation needs a user id the companies API may not return. */
  adminUserId(company: Company | null | undefined): string {
    return (company?.admin_user_id || '').trim();
  }

  canImpersonate(company: Company | null | undefined): boolean {
    return !!this.adminUserId(company);
  }

  adminLabel(company: Company | null | undefined): string {
    return company?.admin_name || company?.admin_email || 'shop admin';
  }

  warnMissingAdminId(): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Cannot impersonate',
      detail:
        'This company has no admin user_id. Backend must return admin/owner id on companies list.',
      life: 6000,
    });
  }

  /**
   * Opens the session in a new tab. `onSettled` fires once the request resolves,
   * whether or not a session started, so callers can close their confirm dialog.
   */
  start(company: Company, onSettled?: () => void): void {
    const userId = this.adminUserId(company);
    if (!userId) {
      this.warnMissingAdminId();
      onSettled?.();
      return;
    }
    if (this.busyCompanyId) {
      return;
    }

    this.busyCompanyId = company.id;
    this.api
      .impersonateAdmin({ user_id: userId, company_id: company.id, reason: 'support' })
      .subscribe({
        next: res => {
          this.busyCompanyId = null;
          handleImpersonationApiResult(
            res,
            data =>
              this.impersonation.startFromAdminImpersonateApi(data, {
                company_id: company.id,
                company_name: company.name,
                timezone: company.timezone,
              }),
            this.messageService,
            'No token returned from impersonate/admin.'
          );
          onSettled?.();
        },
        error: err => {
          this.busyCompanyId = null;
          notifyImpersonationFailure(
            this.messageService,
            err?.error?.message || err?.message || 'Request failed.'
          );
          onSettled?.();
        },
      });
  }
}
